import type { PlaybackRequestSource } from "../api/queries/segments";
import type { PlayerPrefs, User } from "../api/types";

export type Filter = Record<string, string | number | boolean>;
export type PlaybackCommand =
  | { type: "open"; source: PlaybackRequestSource; filter: Filter }
  | { type: "restore"; prefs: PlayerPrefs }
  | { type: "play" | "pause" | "stop" }
  | {
      type: "seek";
      target:
        { kind: "source"; atMs: number; segmentId?: number } | { kind: "media"; mediaUs: number };
    }
  | { type: "step"; direction: -1 | 1 }
  | { type: "nudge"; seconds: number }
  | { type: "volume"; volume: number; muted: boolean }
  | { type: "offline"; downloadId: string; itemPositionUs: number };

export interface PlaybackSnapshot {
  version: 1;
  sessionId: string;
  accountEpoch: number;
  accountId: number | null;
  access: "anonymous" | "authenticated" | "offline-profile" | "reauth-required" | "revoked";
  generation: number;
  revision: number;
  state: "idle" | "loading" | "playing" | "paused" | "buffering" | "ended" | "error";
  wantsPlayback: boolean;
  source: PlaybackRequestSource | null;
  presentationId: string | null;
  downloadId: string | null;
  mediaUs: number | null;
  sourceAt: string | null;
  atLive: boolean;
  audible: null | {
    segmentId: number | null;
    title: string | null;
    subtitle: string | null;
    artworkPath: string | null;
    itemPositionUs: number;
    itemDurationUs: number | null;
  };
  prefs: PlayerPrefs;
  listened: Array<[number, number]>;
  error: null | { code: string; retryable: boolean };
}

export interface NativeFile {
  id: string;
  name: string;
  mime: string;
  size: number;
}

export interface NativeSocket {
  send(data: string): void;
  close(): void;
}

export interface SocketEvents {
  open(): void;
  message(data: string): void;
  close(): void;
  error(): void;
}

export interface DownloadEntry {
  id: string;
  segmentId: number;
  assetId: string;
  title: string;
  state: "queued" | "downloading" | "paused" | "verifying" | "failed" | "ready";
  bytes: number;
  totalBytes: number;
  allowMetered: boolean;
  errorCode: string | null;
}

export interface DownloadSnapshot {
  entries: DownloadEntry[];
  usedBytes: number;
  reservedBytes: number;
  budgetBytes: number | null;
  freeBytes: number;
}

export type DownloadCommand =
  | { type: "add"; segmentId: number; allowMetered: boolean }
  | { type: "pause" | "retry" | "cancel" | "remove"; id: string }
  | { type: "budget"; bytes: number };

export interface NativeRuntime {
  readonly kind: "android";
  request(path: string, init?: RequestInit): Promise<Response>;
  connectRealtime(events: SocketEvents): NativeSocket;
  credentialSignIn(body: {
    username: string;
    password: string;
    captcha_token?: string;
  }): Promise<User>;
  solveCaptcha(endpoint: string, signal?: AbortSignal): Promise<string>;
  signIn(purpose: "login" | "account", provider?: string): Promise<User>;
  logout(): Promise<{ remoteRevoked: boolean }>;
  snapshot(): Promise<PlaybackSnapshot>;
  command(command: PlaybackCommand): Promise<void>;
  subscribe(listener: (snapshot: PlaybackSnapshot) => void): Promise<() => void>;
  downloads(): Promise<DownloadSnapshot>;
  download(command: DownloadCommand): Promise<void>;
  subscribeDownloads(listener: (snapshot: DownloadSnapshot) => void): Promise<() => void>;
  pickFile(mime: string[]): Promise<NativeFile | null>;
  upload(
    path: string,
    field: string,
    file: NativeFile,
    fields?: Record<string, string>,
  ): Promise<Response>;
  acquireResource(path: string): Promise<{ id: string; url: string }>;
  releaseResource(id: string): Promise<void>;
  saveFile(path: string): Promise<void>;
  openExternal(url: string): Promise<void>;
}

let runtime: NativeRuntime | null = null;

export function installNativeRuntime(value: NativeRuntime): void {
  if (runtime !== null) throw new Error("Native runtime already installed");
  runtime = value;
}

export function nativeRuntime(): NativeRuntime | null {
  return runtime;
}
